/*
 * Domiciliation SEPA (schéma CORE) de Sun Is Up ASBL.
 * - Contrôles : IBAN (longueur du pays et clé mod 97), BIC, identifiant créancier.
 * - Calendrier TARGET2 : jours ouvrables bancaires (art. 5 du contrat BNP Paribas Fortis).
 * - Fichier XML pain.008 (version 001.08, ou 001.02 en option) importé dans Easy Banking Business.
 * Module sans dépendance, partagé entre le CRM (navigateur) et la fonction create-sepa-batch.
 */

export type SepaFormat = 'pain.008.001.08' | 'pain.008.001.02';
export const SEPA_FORMATS: SepaFormat[] = ['pain.008.001.08', 'pain.008.001.02'];
export type SequenceType = 'FRST' | 'RCUR';

/* ── IBAN, BIC, identifiant créancier ── */

/** Longueur de l'IBAN des pays de la zone SEPA. */
const IBAN_LENGTHS: Record<string, number> = {
  AD: 24, AL: 28, AT: 20, BE: 16, BG: 22, CH: 21, CY: 28, CZ: 24, DE: 22, DK: 18, EE: 20, ES: 24, FI: 18, FR: 27,
  GB: 22, GG: 22, GI: 23, GR: 27, HR: 21, HU: 28, IE: 22, IM: 22, IS: 26, IT: 27, JE: 22, LI: 21, LT: 20, LU: 20,
  LV: 21, MC: 27, MD: 24, ME: 22, MK: 19, MT: 31, NL: 18, NO: 15, PL: 28, PT: 25, RO: 24, SE: 24, SI: 19, SK: 24,
  SM: 27, VA: 22,
};

/** Reste de la division par 97 d'une chaîne alphanumérique (lettres : A = 10 … Z = 35). */
function mod97(value: string): number {
  let rest = 0;
  for (const ch of value) {
    const digits = ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of digits) rest = (rest * 10 + Number(d)) % 97;
  }
  return rest;
}

export const normalizeIban = (v: string | null | undefined) => (v ?? '').replace(/\s+/g, '').toUpperCase();

/** Motif du refus d'un IBAN, ou null s'il est valide pour un prélèvement SEPA. */
export function ibanError(v: string | null | undefined): string | null {
  const s = normalizeIban(v);
  if (!s) return 'IBAN manquant';
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(s)) return 'IBAN invalide (ex. BE68 5390 0754 7034)';
  const length = IBAN_LENGTHS[s.slice(0, 2)];
  if (!length) return `Pays ${s.slice(0, 2)} hors zone SEPA`;
  if (s.length !== length) return `Un IBAN ${s.slice(0, 2)} compte ${length} caractères`;
  if (mod97(s.slice(4) + s.slice(0, 4)) !== 1) return 'IBAN invalide : la clé de contrôle ne correspond pas';
  return null;
}

export const isValidIban = (v: string | null | undefined) => ibanError(v) === null;

/** BE68539007547034 -> BE68 5390 0754 7034 */
export const formatIban = (v: string | null | undefined) => normalizeIban(v).replace(/(.{4})/g, '$1 ').trim();

/** BE68539007547034 -> BE68 **** **** 7034 */
export function maskIban(v: string | null | undefined): string {
  const s = normalizeIban(v);
  return s.length < 8 ? s : `${s.slice(0, 4)} **** **** ${s.slice(-4)}`;
}

export const normalizeBic = (v: string | null | undefined) => (v ?? '').replace(/\s+/g, '').toUpperCase();
export const isValidBic = (v: string | null | undefined) => /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(normalizeBic(v));

/** Identifiant créancier SEPA (ex. BE76ZZZ1022108004) : pays, clé, code activité, identifiant national. */
export function isValidCreditorId(v: string | null | undefined): boolean {
  const s = normalizeIban(v);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{3}[A-Z0-9]{1,28}$/.test(s)) return false;
  return mod97(s.slice(7) + s.slice(0, 4)) === 1;
}

/* ── Calendrier TARGET2 ── */

const pad = (n: number) => String(n).padStart(2, '0');
const parseIso = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00Z`);
const toIso = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

export function addDays(iso: string, n: number): string {
  const d = parseIso(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return toIso(d);
}

/** Nombre de jours calendrier entre deux dates (to - from). */
export const daysBetween = (from: string, to: string) => Math.round((parseIso(to).getTime() - parseIso(from).getTime()) / 86_400_000);

/** Dimanche de Pâques (calendrier grégorien). */
export function easterSunday(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Jours de fermeture de TARGET2 (hors week-end) : 1er janvier, Vendredi saint, lundi de Pâques, 1er mai, 25 et 26 décembre. */
export function targetHolidays(year: number): string[] {
  const easter = easterSunday(year);
  return [`${year}-01-01`, addDays(easter, -2), addDays(easter, 1), `${year}-05-01`, `${year}-12-25`, `${year}-12-26`];
}

export function isTargetBusinessDay(iso: string): boolean {
  const d = parseIso(iso);
  const weekday = d.getUTCDay();
  if (weekday === 0 || weekday === 6) return false;
  return !targetHolidays(d.getUTCFullYear()).includes(iso.slice(0, 10));
}

/** Date située `n` jours ouvrables TARGET2 après `iso`. */
export function addTargetBusinessDays(iso: string, n: number): string {
  let d = iso.slice(0, 10);
  for (let count = 0; count < n;) {
    d = addDays(d, 1);
    if (isTargetBusinessDay(d)) count++;
  }
  return d;
}

/** Jour ouvrable TARGET2 qui précède `iso`. */
export function previousTargetBusinessDay(iso: string): string {
  let d = addDays(iso, -1);
  while (!isTargetBusinessDay(d)) d = addDays(d, -1);
  return d;
}

/** Nombre de jours ouvrables TARGET2 d tels que from < d ≤ to. */
export function targetBusinessDaysBetween(from: string, to: string): number {
  let count = 0;
  for (let d = addDays(from, 1); d <= to; d = addDays(d, 1)) if (isTargetBusinessDay(d)) count++;
  return count;
}

/** Date et heure à Bruxelles. */
export function brusselsNow(now: Date = new Date()): { date: string; time: string; hour: number } {
  const parts = new Intl.DateTimeFormat('fr-BE', {
    timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(now);
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? '00';
  return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}:${get('second')}`, hour: Number(get('hour')) };
}

export interface CollectionRules {
  /** Délai minimum entre l'envoi de la facture et le prélèvement (jours ouvrables TARGET2). */
  minBusinessDays: number;
  /** Délai maximum (jours calendrier). */
  maxCalendarDays: number;
}

export const proposeCollectionDate = (today: string, rules: CollectionRules) => addTargetBusinessDays(today, rules.minBusinessDays);

const frDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

/** Motif du refus d'une date de prélèvement, ou null si elle convient. */
export function collectionDateError(date: string, today: string, rules: CollectionRules): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return 'Date de prélèvement invalide';
  if (!isTargetBusinessDay(date)) return 'Ce jour n’est pas un jour ouvrable bancaire (week-end ou jour de fermeture TARGET2)';
  if (targetBusinessDaysBetween(today, date) < rules.minBusinessDays) {
    return `Au moins ${rules.minBusinessDays} jours ouvrables après aujourd’hui : le ${frDate(proposeCollectionDate(today, rules))} au plus tôt`;
  }
  if (daysBetween(today, date) > rules.maxCalendarDays) return `Au plus ${rules.maxCalendarDays} jours après aujourd’hui : le ${frDate(addDays(today, rules.maxCalendarDays))} au plus tard`;
  return null;
}

/** Date limite d'import dans Easy Banking Business : veille ouvrée du prélèvement, à l'heure limite de la banque. */
export const importDeadlineDate = (collectionDate: string) => previousTargetBusinessDay(collectionDate);

/* ── Texte autorisé dans un fichier SEPA ── */

const SPECIAL: Record<string, string> = {
  'ß': 'ss', 'ẞ': 'SS', 'Æ': 'AE', 'æ': 'ae', 'Œ': 'OE', 'œ': 'oe', 'Ø': 'O', 'ø': 'o', 'Ł': 'L', 'ł': 'l', 'Đ': 'D', 'đ': 'd',
  '&': '+', '’': "'", '‘': "'", '´': "'", '`': "'", '«': '', '»': '', '“': '', '”': '', '"': '', '–': '-', '—': '-', '_': '-',
};

/** Jeu de caractères latin du SEPA : lettres sans accent, chiffres, espace et / - ? : ( ) . , ' + */
export function sepaText(value: string | null | undefined, max: number): string {
  let s = Array.from(value ?? '').map(ch => SPECIAL[ch] ?? ch).join('').normalize('NFD').replace(/[̀-ͯ]/g, '');
  s = s.replace(/[^A-Za-z0-9/\-?:().,'+ ]/g, ' ').replace(/\s+/g, ' ').replace(/\/{2,}/g, '/').trim();
  return s.slice(0, max).replace(/^\/+|\/+$/g, '').trim();
}

/* ── Fichier pain.008 ── */

export interface SepaCreditor {
  name: string;
  iban: string;
  bic: string;
  creditorId: string;
}

export interface SepaTransaction {
  /** Numéro de la facture (identifiant de bout en bout, 35 caractères au plus). */
  endToEndId: string;
  /** Montant en centimes (strictement positif). */
  amountCents: number;
  mandateId: string;
  /** Date de signature du mandat (AAAA-MM-JJ). */
  mandateSignedAt: string;
  debtorName: string;
  debtorIban: string;
  debtorBic?: string | null;
  /** Communication non structurée (140 caractères au plus). */
  remittance: string;
  sequenceType: SequenceType;
}

export interface SepaBatchInput {
  format: SepaFormat;
  msgId: string;
  /** Date et heure de création (AAAA-MM-JJTHH:MM:SS). */
  createdAt: string;
  initiatingParty: string;
  creditor: SepaCreditor;
  /** Date de prélèvement demandée (AAAA-MM-JJ). */
  collectionDate: string;
  transactions: SepaTransaction[];
}

const xmlEscape = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
export const formatCents = (cents: number) => `${Math.floor(cents / 100)}.${pad(cents % 100)}`;

/** Montant en euros (nombre à 2 décimales) -> centimes. */
export const toCents = (amount: number | string) => Math.round(Number(amount) * 100);

/** Motifs de refus du lot (vide = valide). Les contrôles métier (mandat actif, plafonds) sont faits avant. */
export function batchErrors(input: SepaBatchInput): string[] {
  const errors: string[] = [];
  const c = input.creditor;
  if (!sepaText(c.name, 70)) errors.push('Nom du créancier manquant');
  if (!isValidIban(c.iban)) errors.push('IBAN du créancier invalide');
  if (!isValidBic(c.bic)) errors.push('BIC du créancier invalide');
  if (!isValidCreditorId(c.creditorId)) errors.push('Identifiant créancier invalide');
  if (!/^[A-Za-z0-9-]{1,30}$/.test(input.msgId)) errors.push('Identifiant du fichier invalide');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.collectionDate) || !isTargetBusinessDay(input.collectionDate)) errors.push('Date de prélèvement invalide');
  if (input.transactions.length === 0) errors.push('Aucun prélèvement');
  const ids = new Set<string>();
  for (const t of input.transactions) {
    const label = t.endToEndId || '(sans numéro)';
    if (!/^[A-Za-z0-9-]{1,35}$/.test(t.endToEndId)) errors.push(`${label} : numéro de facture invalide pour un fichier SEPA`);
    if (ids.has(t.endToEndId)) errors.push(`${label} : numéro en double`);
    ids.add(t.endToEndId);
    if (!Number.isInteger(t.amountCents) || t.amountCents <= 0) errors.push(`${label} : montant invalide`);
    if (!/^[A-Za-z0-9-]{1,35}$/.test(t.mandateId)) errors.push(`${label} : référence de mandat invalide`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t.mandateSignedAt)) errors.push(`${label} : date de signature du mandat manquante`);
    if (!sepaText(t.debtorName, 70)) errors.push(`${label} : nom du débiteur manquant`);
    const ibanErr = ibanError(t.debtorIban);
    if (ibanErr) errors.push(`${label} : ${ibanErr}`);
    if (t.debtorBic && !isValidBic(t.debtorBic)) errors.push(`${label} : BIC invalide`);
  }
  return errors;
}

/** Fichier XML d'initiation de prélèvements (un bloc par type de séquence : FRST puis RCUR). */
export function buildPain008(input: SepaBatchInput): string {
  const v08 = input.format === 'pain.008.001.08';
  const bicTag = v08 ? 'BICFI' : 'BIC';
  const agent = (bic: string | null | undefined) => (bic && isValidBic(bic)
    ? `<FinInstnId><${bicTag}>${normalizeBic(bic)}</${bicTag}></FinInstnId>`
    : '<FinInstnId><Othr><Id>NOTPROVIDED</Id></Othr></FinInstnId>');
  const sum = (list: SepaTransaction[]) => list.reduce((acc, t) => acc + t.amountCents, 0);
  const c = input.creditor;

  const blocks = (['FRST', 'RCUR'] as SequenceType[])
    .map(seq => ({ seq, list: input.transactions.filter(t => t.sequenceType === seq) }))
    .filter(b => b.list.length > 0)
    .map(({ seq, list }) => [
      '    <PmtInf>',
      `      <PmtInfId>${xmlEscape(`${input.msgId}-${seq}`)}</PmtInfId>`,
      '      <PmtMtd>DD</PmtMtd>',
      '      <BtchBookg>true</BtchBookg>',
      `      <NbOfTxs>${list.length}</NbOfTxs>`,
      `      <CtrlSum>${formatCents(sum(list))}</CtrlSum>`,
      `      <PmtTpInf><SvcLvl><Cd>SEPA</Cd></SvcLvl><LclInstrm><Cd>CORE</Cd></LclInstrm><SeqTp>${seq}</SeqTp></PmtTpInf>`,
      `      <ReqdColltnDt>${input.collectionDate}</ReqdColltnDt>`,
      `      <Cdtr><Nm>${xmlEscape(sepaText(c.name, 70))}</Nm></Cdtr>`,
      `      <CdtrAcct><Id><IBAN>${normalizeIban(c.iban)}</IBAN></Id></CdtrAcct>`,
      `      <CdtrAgt>${agent(c.bic)}</CdtrAgt>`,
      '      <ChrgBr>SLEV</ChrgBr>',
      `      <CdtrSchmeId><Id><PrvtId><Othr><Id>${normalizeIban(c.creditorId)}</Id><SchmeNm><Prtry>SEPA</Prtry></SchmeNm></Othr></PrvtId></Id></CdtrSchmeId>`,
      ...list.map(t => [
        '      <DrctDbtTxInf>',
        `        <PmtId><EndToEndId>${xmlEscape(t.endToEndId)}</EndToEndId></PmtId>`,
        `        <InstdAmt Ccy="EUR">${formatCents(t.amountCents)}</InstdAmt>`,
        `        <DrctDbtTx><MndtRltdInf><MndtId>${xmlEscape(t.mandateId)}</MndtId><DtOfSgntr>${t.mandateSignedAt.slice(0, 10)}</DtOfSgntr></MndtRltdInf></DrctDbtTx>`,
        `        <DbtrAgt>${agent(t.debtorBic)}</DbtrAgt>`,
        `        <Dbtr><Nm>${xmlEscape(sepaText(t.debtorName, 70))}</Nm></Dbtr>`,
        `        <DbtrAcct><Id><IBAN>${normalizeIban(t.debtorIban)}</IBAN></Id></DbtrAcct>`,
        `        <RmtInf><Ustrd>${xmlEscape(sepaText(t.remittance, 140))}</Ustrd></RmtInf>`,
        '      </DrctDbtTxInf>',
      ].join('\n')),
      '    </PmtInf>',
    ].join('\n'));

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<Document xmlns="urn:iso:std:iso:20022:tech:xsd:${input.format}" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">`,
    '  <CstmrDrctDbtInitn>',
    '    <GrpHdr>',
    `      <MsgId>${xmlEscape(input.msgId)}</MsgId>`,
    `      <CreDtTm>${input.createdAt.slice(0, 19)}</CreDtTm>`,
    `      <NbOfTxs>${input.transactions.length}</NbOfTxs>`,
    `      <CtrlSum>${formatCents(sum(input.transactions))}</CtrlSum>`,
    `      <InitgPty><Nm>${xmlEscape(sepaText(input.initiatingParty, 70))}</Nm></InitgPty>`,
    '    </GrpHdr>',
    ...blocks,
    '  </CstmrDrctDbtInitn>',
    '</Document>',
    '',
  ].join('\n');
}
